import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, Clock, CalendarDays, Facebook, MessageCircle } from "lucide-react";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Reveal } from "@/lib/Reveal";
import { BUSINESS } from "@/lib/data";
import { POSTS } from "@/lib/blog";

export default function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Teign Gas`;
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", post.excerpt);
    window.scrollTo(0, 0);
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "post-schema";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": `https://teigngas.com${post.image}`,
      "inLanguage": "en-GB",
      "author": { "@type": ["Plumber", "HVACBusiness"], "name": "Teign Gas", "url": "https://teigngas.com/" },
      "publisher": { "@type": "Organization", "name": "Teign Gas", "logo": { "@type": "ImageObject", "url": "https://teigngas.com/images/logo-full.png" } },
    });
    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = "post-bc-schema";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://teigngas.com/" },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://teigngas.com/blog" },
        { "@type": "ListItem", "position": 3, "name": post.title },
      ],
    });
    document.head.appendChild(schema);
    document.head.appendChild(bc);
    return () => { schema.remove(); bc.remove(); };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-display font-extrabold text-2xl text-white uppercase">Article not found</h1>
          <Link to="/" className="mt-6 inline-block bg-torch text-white font-display font-bold uppercase px-7 py-3 rounded-sm">Back to homepage</Link>
        </div>
      </div>
    );
  }

  const others = POSTS.filter((p) => p.slug !== slug);

  return (
    <div data-testid="blog-post-page" className="bg-obsidian text-neutral-100 overflow-x-clip">
      <div className="grain-overlay" />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="post-logo" className="flex items-center shrink-0">
            <img src="/images/logo-flame.png" alt="Teign Gas" className="h-9 w-9 sm:hidden mix-blend-screen" />
            <img src="/images/logo-mark.png" alt="Teign Gas — Service, Repair, Install" className="hidden sm:block h-9 w-auto mix-blend-screen" />
          </Link>
          <div className="flex items-center gap-3">
            <a href={BUSINESS.phoneHref} data-testid="post-phone-link" className="hidden sm:flex items-center gap-2 font-mono text-xs tracking-wider text-neutral-300 border border-neutral-700 px-4 py-2.5 rounded-sm hover:border-torch hover:text-torch transition-colors">
              <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
            </a>
            <Link to="/#booking" data-testid="post-book-btn" className="bg-torch hover:bg-torch-hover text-white font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 rounded-sm transition-all">
              Book Online
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-32 pb-16">
        <article className="max-w-3xl mx-auto px-4 sm:px-8">
          <Reveal>
            <Link to="/#blog" data-testid="post-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-neutral-500 hover:text-torch transition-colors mb-8">
              <ArrowLeft className="w-3.5 h-3.5" /> All articles
            </Link>
            <div className="flex items-center gap-4 mb-5">
              <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-torch uppercase"><CalendarDays className="w-3 h-3" /> {post.date}</span>
              <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-neutral-500 uppercase"><Clock className="w-3 h-3" /> {post.readTime}</span>
            </div>
            <h1 data-testid="post-title" className="font-display font-extrabold tracking-tight leading-tight text-3xl sm:text-4xl text-white">
              {post.title}
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 border border-neutral-800 rounded-sm overflow-hidden">
              <img src={post.image} alt={post.title} className="w-full h-64 sm:h-80 object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <div className="mt-10 space-y-6" data-testid="post-content">
              {post.content.map((para, i) => (
                <p key={i} className="text-neutral-300 leading-relaxed text-base">{para}</p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 border border-torch/30 bg-torch/5 rounded-sm p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <p className="text-neutral-200 text-sm sm:text-base max-w-md">
                Need a hand? Fixed prices, online booking, and a Gas Safe engineer at your door across South Devon.
              </p>
              <Link to="/#booking" data-testid="post-cta-btn" className="group shrink-0 bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-6 py-3.5 rounded-sm flex items-center gap-2.5 transition-all">
                Book online
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-8 flex items-center gap-3">
            <span className="font-mono text-[10px] tracking-[0.25em] text-neutral-500 uppercase">Share this</span>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://teigngas.com/blog/${post.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="post-share-facebook"
              aria-label="Share on Facebook"
              className="w-9 h-9 border border-neutral-800 hover:border-torch text-neutral-400 hover:text-torch rounded-sm flex items-center justify-center transition-colors"
            >
              <Facebook className="w-4 h-4" />
            </a>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${post.title} — https://teigngas.com/blog/${post.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="post-share-whatsapp"
              aria-label="Share on WhatsApp"
              className="w-9 h-9 border border-neutral-800 hover:border-torch text-neutral-400 hover:text-torch rounded-sm flex items-center justify-center transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
            </a>
          </div>

          <div className="mt-14 pt-8 border-t border-neutral-800">
            <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-5">More from the van</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {others.map((o) => (
                <Link key={o.slug} to={`/blog/${o.slug}`} data-testid={`post-related-${o.slug}`} className="group border border-neutral-800 hover:border-torch rounded-sm p-5 transition-colors">
                  <span className="font-mono text-[10px] tracking-[0.2em] text-torch uppercase">{o.date}</span>
                  <p className="mt-2 font-display font-semibold text-neutral-200 group-hover:text-white leading-snug transition-colors">{o.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
    </div>
  );
}
